import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import { FontSize } from '../hooks/useFontSize';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/viewPage.css';
import '../styles/components/displayModal.css';

interface ViewSettingsModalProps {
  onClose: () => void;
  fontSize: FontSize;
  onFontSizeChange: (size: FontSize) => void;
}

const themeDescriptions: Record<string, string> = {
  light: 'Clean and bright with blue accents',
  pastel: 'Soft tones with gentle colors',
  dark: 'Easy on the eyes in low light',
  system: 'Follows your device appearance',
};

const pastelDescriptions: Record<string, string> = {
  claire: 'Warm lavender with periwinkle accents',
  soft: 'Cool blue inspired by the wandering sock',
  earth: 'Lilac and plum with a gentle touch',
  sausalito: 'Coastal greens and sage tones',
};

const fontSizeDescriptions: Record<string, string> = {
  small: 'Compact text for more content on screen',
  medium: 'Default text size, balanced readability',
  large: 'Larger text for easier reading',
  'x-large': 'Extra large text for accessibility',
};

const themeColors: Record<string, { bg: string; text: string; accent: string; border: string }> = {
  light: { bg: '#ffffff', text: '#1d1d1f', accent: '#007AFF', border: '#e5e7eb' },
  dark: { bg: '#1c1c1e', text: '#f0f0f0', accent: '#0A84FF', border: '#48484a' },
};

const pastelColors: Record<string, { bg: string; text: string; accent: string; border: string }> = {
  claire: { bg: '#f8f4f0', text: '#5a64a0', accent: '#3b4580', border: '#d5c8cd' },
  soft: { bg: '#f8f9fa', text: '#3685C0', accent: '#3070A8', border: '#dce1e6' },
  earth: { bg: '#f0efeb', text: '#7a5890', accent: '#583a72', border: '#d8d5ce' },
  sausalito: { bg: '#F9F9F9', text: '#3e8878', accent: '#2e7466', border: '#d5dde0' },
};

const ViewSettingsModal: React.FC<ViewSettingsModalProps> = ({ onClose, fontSize, onFontSizeChange }) => {
  const { preference: themePref, setPreference: setThemePref, pastelVariant, setPastelVariant } = useTheme();

  const getThemeButtonStyle = (theme: string): React.CSSProperties | undefined => {
    if (themePref !== theme) return undefined;
    if (theme === 'pastel') {
      const c = pastelColors[pastelVariant] || pastelColors.claire;
      return { background: c.bg, color: c.text, borderColor: c.accent };
    }
    if (theme === 'system') return undefined;
    const c = themeColors[theme];
    if (!c) return undefined;
    return { background: c.bg, color: c.text, borderColor: c.accent };
  };

  const getThemeLabel = () => {
    if (themePref === 'pastel') {
      const variantName = { claire: 'Claire', soft: 'Sock', earth: 'Lilac', sausalito: 'Sausalito' }[pastelVariant] || 'Claire';
      return `Pastel · ${variantName}`;
    }
    return { light: 'Light', dark: 'Dark', system: 'System' }[themePref] || 'System';
  };

  const getThemeDescription = () => {
    if (themePref === 'pastel') return pastelDescriptions[pastelVariant] || pastelDescriptions.claire;
    return themeDescriptions[themePref] || themeDescriptions.system;
  };

  // Image size state — reads/writes same localStorage key as InventoryView
  const [thumbnailSize, setThumbnailSize] = useState<'small' | 'medium' | 'large' | 'x-large'>(() => {
    const saved = localStorage.getItem('inventory-thumbnailSize');
    return (saved as 'small' | 'medium' | 'large' | 'x-large') || 'medium';
  });

  const handleThumbnailSizeChange = (size: 'small' | 'medium' | 'large' | 'x-large') => {
    setThumbnailSize(size);
    localStorage.setItem('inventory-thumbnailSize', size);
    window.dispatchEvent(new StorageEvent('storage', { key: 'inventory-thumbnailSize', newValue: size }));
  };

  return ReactDOM.createPortal(
    <div
      className="modal-overlay display-modal-overlay"
      onClick={onClose}
      style={{ zIndex: 10001 }}
    >
      <div
        className="display-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-bg-primary)', position: 'relative' }}
      >
        <ModalCloseButton onClick={onClose} style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 1 }} />
        <div className="modal-body" style={{ padding: '12px 16px' }}>
          <div className="view-page" style={{ padding: 0, flex: 'none' }}>
            {/* Theme */}
            <div className="view-section">
              <h4 className="view-section-title">Theme</h4>
              <div className="display-options theme-options">
                <button
                  className={`display-option ${themePref === 'light' ? 'active' : ''}`}
                  onClick={() => setThemePref('light')}
                  style={getThemeButtonStyle('light')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="5"></circle>
                    <line x1="12" y1="1" x2="12" y2="3"></line>
                    <line x1="12" y1="21" x2="12" y2="23"></line>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                    <line x1="1" y1="12" x2="3" y2="12"></line>
                    <line x1="21" y1="12" x2="23" y2="12"></line>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                  </svg>
                  <span>Light</span>
                </button>
                <button
                  className={`display-option ${themePref === 'pastel' ? 'active' : ''}`}
                  onClick={() => setThemePref('pastel')}
                  style={getThemeButtonStyle('pastel')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"></path>
                    <path d="M8 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" fill="currentColor" opacity="0.3"></path>
                    <path d="M16 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" fill="currentColor" opacity="0.3"></path>
                    <path d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" fill="currentColor" opacity="0.3"></path>
                  </svg>
                  <span>Pastel</span>
                </button>
                <button
                  className={`display-option ${themePref === 'dark' ? 'active' : ''}`}
                  onClick={() => setThemePref('dark')}
                  style={getThemeButtonStyle('dark')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                  </svg>
                  <span>Dark</span>
                </button>
                <button
                  className={`display-option ${themePref === 'system' ? 'active' : ''}`}
                  onClick={() => setThemePref('system')}
                  style={getThemeButtonStyle('system')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                    <line x1="8" y1="21" x2="16" y2="21"></line>
                    <line x1="12" y1="17" x2="12" y2="21"></line>
                  </svg>
                  <span>System</span>
                </button>
              </div>
              {themePref === 'pastel' && (
                <div className="pastel-variant-picker">
                  <button
                    className={`pastel-swatch pastel-swatch-claire ${pastelVariant === 'claire' ? 'active' : ''}`}
                    onClick={() => setPastelVariant('claire')}
                    title="Claire"
                  >
                    <span className="pastel-swatch-color"><span style={{ background: '#f8f4f0' }}></span><span style={{ background: '#5c66a0' }}></span></span>
                    <span>Claire</span>
                  </button>
                  <button
                    className={`pastel-swatch pastel-swatch-soft ${pastelVariant === 'soft' ? 'active' : ''}`}
                    onClick={() => setPastelVariant('soft')}
                    title="Sock"
                  >
                    <span className="pastel-swatch-color"><span style={{ background: '#f8f9fa' }}></span><span style={{ background: '#3685C0' }}></span></span>
                    <span>Sock</span>
                  </button>
                  <button
                    className={`pastel-swatch pastel-swatch-earth ${pastelVariant === 'earth' ? 'active' : ''}`}
                    onClick={() => setPastelVariant('earth')}
                    title="Lilac"
                  >
                    <span className="pastel-swatch-color"><span style={{ background: '#f0efeb' }}></span><span style={{ background: '#7a5890' }}></span></span>
                    <span>Lilac</span>
                  </button>
                  <button
                    className={`pastel-swatch pastel-swatch-sausalito ${pastelVariant === 'sausalito' ? 'active' : ''}`}
                    onClick={() => setPastelVariant('sausalito')}
                    title="Sausalito"
                  >
                    <span className="pastel-swatch-color"><span style={{ background: '#F9F9F9' }}></span><span style={{ background: '#5a9e90' }}></span></span>
                    <span>Sausalito</span>
                  </button>
                </div>
              )}

              {/* Theme label + description */}
              <div className="view-preview theme-preview">
                <div className="preview-label">{getThemeLabel()}</div>
                <div className="preview-description">{getThemeDescription()}</div>
              </div>

              {/* Color samples */}
              <div className="preview-dots">
                <div className="preview-dot-group">
                  <span className="preview-dot" style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)' }} />
                  <span className="preview-dot-label">Background</span>
                </div>
                <div className="preview-dot-group">
                  <span className="preview-dot" style={{ background: 'var(--color-accent)' }} />
                  <span className="preview-dot-label">Accent</span>
                </div>
                <div className="preview-dot-group">
                  <span className="preview-dot" style={{ background: 'var(--color-success)' }} />
                  <span className="preview-dot-label">Success</span>
                </div>
                <div className="preview-dot-group">
                  <span className="preview-dot" style={{ background: 'var(--color-warning)' }} />
                  <span className="preview-dot-label">Warning</span>
                </div>
                <div className="preview-dot-group">
                  <span className="preview-dot" style={{ background: 'var(--color-text-primary)' }} />
                  <span className="preview-dot-label">Text</span>
                </div>
              </div>
            </div>

            {/* Image Size */}
            <div className="view-section">
              <h4 className="view-section-title">Image Size</h4>
              <div className="display-options" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px' }}>
                <button
                  className={`display-option ${thumbnailSize === 'small' ? 'active' : ''}`}
                  onClick={() => handleThumbnailSizeChange('small')}
                  style={{ flexDirection: 'column', padding: '8px' }}
                >
                  <svg width="24" height="24" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="8" width="7" height="7" rx="1"></rect>
                    <line x1="14" y1="9.5" x2="24" y2="9.5"></line>
                    <line x1="14" y1="13" x2="20" y2="13"></line>
                  </svg>
                  <span style={{ fontSize: '11px' }}>Small</span>
                </button>
                <button
                  className={`display-option ${thumbnailSize === 'medium' ? 'active' : ''}`}
                  onClick={() => handleThumbnailSizeChange('medium')}
                  style={{ flexDirection: 'column', padding: '8px' }}
                >
                  <svg width="24" height="24" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="5" width="10" height="10" rx="1.5"></rect>
                    <line x1="17" y1="7" x2="24" y2="7"></line>
                    <line x1="17" y1="11" x2="22" y2="11"></line>
                  </svg>
                  <span style={{ fontSize: '11px' }}>Medium</span>
                </button>
                <button
                  className={`display-option ${thumbnailSize === 'large' ? 'active' : ''}`}
                  onClick={() => handleThumbnailSizeChange('large')}
                  style={{ flexDirection: 'column', padding: '8px' }}
                >
                  <svg width="24" height="24" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="13" height="13" rx="1.5"></rect>
                    <line x1="19" y1="7" x2="25" y2="7"></line>
                    <line x1="19" y1="11" x2="24" y2="11"></line>
                    <line x1="19" y1="15" x2="22" y2="15"></line>
                  </svg>
                  <span style={{ fontSize: '11px' }}>Large</span>
                </button>
                <button
                  className={`display-option ${thumbnailSize === 'x-large' ? 'active' : ''}`}
                  onClick={() => handleThumbnailSizeChange('x-large')}
                  style={{ flexDirection: 'column', padding: '8px' }}
                >
                  <svg width="24" height="24" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="2" width="20" height="20" rx="1.5"></rect>
                    <line x1="9" y1="24.5" x2="19" y2="24.5"></line>
                  </svg>
                  <span style={{ fontSize: '11px' }}>XL</span>
                </button>
              </div>
            </div>

            {/* Font Size */}
            <div className="view-section">
              <h4 className="view-section-title">Text Size</h4>
              <div className="display-options" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px' }}>
                {(['small', 'medium', 'large', 'x-large'] as FontSize[]).map((size) => (
                  <button
                    key={size}
                    className={`display-option ${fontSize === size ? 'active' : ''}`}
                    onClick={() => onFontSizeChange(size)}
                    style={{ flexDirection: 'column', padding: '8px' }}
                  >
                    <span style={{
                      fontSize: size === 'small' ? '13px' : size === 'medium' ? '16px' : size === 'large' ? '19px' : '22px',
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}>Aa</span>
                    <span style={{ fontSize: '11px' }}>
                      {{ small: 'Small', medium: 'Medium', large: 'Large', 'x-large': 'XL' }[size]}
                    </span>
                  </button>
                ))}
              </div>
              <div className="view-preview size-preview">
                <div className="preview-description">{fontSizeDescriptions[fontSize]}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ViewSettingsModal;
